// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React, { Component, Fragment, createRef } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import * as Sentry from '@sentry/browser'

import {
  ExpandableOverviewItem,
  LabelledDetail,
  Button,
  SpanWithData,
  PicklistWithData,
  TextArea,
  SvgIcon,
  SelectIdentityByResponsibility as SelectReviewer
} from '../'

import { ReadOnlyContext } from '../../context'

const setOutcome = (outcome) => (
  outcome ? outcome.toLowerCase() : null
)


export default class CreditApplicationFinalise extends Component {
  static propTypes = {
    outcome: PropTypes.string,
    outcomeNotes: PropTypes.string,
    open: PropTypes.bool.isRequired,
    credit: PropTypes.shape({
      cmsId: PropTypes.string,
      amount: PropTypes.string,
      instalmentAgreed: PropTypes.nnumber
    }),
    updateCreditApplication: PropTypes.func.isRequired,
    onClick: PropTypes.func.isRequired,
    className: PropTypes.string
  }

  static getDerivedStateFromProps(props, state) {
    // to refresh outcome notes shown, specifically check for "=== Finalisaction removed by ... at ... ===" message that may get added in issue step
    if (
      props.outcomeNotes &&
      props.outcomeNotes.length > state.outcomeNotes.length &&
      props.outcomeNotes.slice(-5).includes('===\n\n') &&  // does last five characters match last five of finaliseation rmoved message
      !state.outcomeNotes.slice(-5).includes('===\n\n')) {
      return {
        outcomeNotes: props.outcomeNotes,
        resetOutcomeNotes: true
      }
    } else {
      if (state.resetOutcomeNotes) {
        return {
          resetOutcomeNotes: false
        }
      } else {
        return null
      }
    }
  }

  constructor(props) {
    super(props)

    this.state = {
      outcome: setOutcome(props.outcome),
      outcomeNotes: props.outcomeNotes || '',
      saveInProgress: false,
      reviewer: null,
      resetOutcomeNotes: false
    }

    this.outcomeNotesRef = createRef()
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.resetOutcomeNotes) {
      // ensure text area (outcome notes) is scrolled to top afer a refresh
      this.outcomeNotesRef.current.scrollTop = 0
    }
  }

  handleOutcomeChange = (value) => this.setState({ outcome: setOutcome(value)  })
  handleOutcomeNotesChange = (e) => this.setState({ outcomeNotes: e.target.value })

  handleClearClick = () => this.setState({
    outcome: setOutcome(this.props.outcome),
    outcomeNotes: this.props.outcomeNotes || '',
    reviewer: null
  })

  handleFinaliseClick = () => {
    this.setState({ saveInProgress: true })

    let now = moment.utc().format("YYYY-MM-DD HH:mm:ss.SSS");

    let params = {
      finalisedBy: localStorage.getItem('userUUID'),
      finalisedAt: now,
      outcome: this.state.outcome,
      outcomeAt: now,
      assignedTo: this.state.reviewer,
      status: 'under_review'
    }

    if (!this.outcomeNotesUnchanged()) {
      params.outcomeNotes = this.state.outcomeNotes
    }

    this.props.updateCreditApplication(params)
    .then(() => this.setState({ saveInProgress: false }))
    .catch(error => {
      Sentry.captureMessage(`Loan application update on finalisation failed - ${error}`)
      this.setState({ saveInProgress: false })
    })
  }

  outcomeUnchanged = () => {
    if (this.props.outcome) {
      return this.state.outcome === this.props.outcome.toLowerCase()
    } else {
      return this.state.outcome === null
    }
  }
  outcomeNotesUnchanged = () => {
    if (this.props.outcomeNotes) {
      return this.state.outcomeNotes === this.props.outcomeNotes
    } else {
      return this.state.outcomeNotes === ''
    }
  }

  assignToReviewer = (e) => this.setState({ reviewer: e.target.value })

  isNonQualifiedOutcome = (outcome) => (
    outcome && ['REFERRED_TODC_INTERESTED', 'REFERRED_TODC_NOTINTERESTED', 'DECLINED', 'EXPIRED', 'CANCELLED'].indexOf(outcome.toUpperCase()) > -1
  )

  render() {
    let { finalisedBy, finalisedAt, outcome, credit, open, onClick, className } = this.props
    let finalised = finalisedBy !== null
    let anyRequiredInputFieldsChanged = !this.outcomeUnchanged() ||
      !this.outcomeNotesUnchanged() || this.state.reviewer
    let allRequiredInputFieldsChanged = !this.outcomeUnchanged() &&
      !this.outcomeNotesUnchanged() && this.state.outcome && this.state.reviewer
    let hasLoanWithRequiredFieldsSaved = credit && credit.cmsId && credit.amount && credit.instalmentAgreed
    let finalisedFeedback = finalised
      ? `Finalised outcome by ${finalisedBy.firstName} ${finalisedBy.lastName}${finalisedAt ? ` at ${moment(finalisedAt).format('lll')}` : ''}`
      : null
    let setFromNonQualifiedOutcome = this.isNonQualifiedOutcome(outcome) && !this.isNonQualifiedOutcome(this.state.outcome)
    let finalisedButQualifiedOutcome = finalised && !this.isNonQualifiedOutcome(outcome)

    return (
      <ExpandableOverviewItem
        title='Finalise'
        summary={finalised ? finalisedFeedback : 'No loan requested'}
        iconSymbol={finalised ? 'check' : null}
        iconColour={finalised ? 'success' : null}
        open={open}
        onClick={onClick}
        className={className}>
        <ReadOnlyContext.Consumer>
          {readOnly => (
            <div className='slds-m-left_small slds-m-top_small slds-m-bottom_medium'>
              <div className='slds-m-bottom_medium slds-size_1-of-2'>
                <LabelledDetail
                  label='Outcome'
                  detail={readOnly || finalised
                    ? <SpanWithData
                        forField='CreditApplicationOutcome'
                        searchKey={outcome}
                      />
                    : <PicklistWithData
                        id='outcomes'
                        placeholder='Select outcome'
                        forField='CreditApplicationOutcome'
                        symbol='chevrondown'
                        value={this.state.outcome || ''}
                        onChange={this.handleOutcomeChange}
                        closeDropdownOnSelect={true}
                        excludeOptions={hasLoanWithRequiredFieldsSaved
                          ? null
                          : ['qualified_fully', 'qualified_partially']
                        }
                        className='slds-m-top_medium slds-m-bottom_medium'
                      />
                  }
                  detailFontSize='inherit'
                />
              </div>
              <div className='slds-size_11-of-12'>
                <LabelledDetail
                  label='Notes'
                  detail={
                    <TextArea
                      ref={this.outcomeNotesRef}
                      placeholder='Add outcome notes'
                      value={this.state.outcomeNotes}
                      onChange={this.handleOutcomeNotesChange}
                      rows={5}
                      disabled={finalisedButQualifiedOutcome || this.outcomeUnchanged() || readOnly}
                    />
                  }
                  detailFontSize='inherit'
                />
              </div>
              {!((finalised && !setFromNonQualifiedOutcome) || readOnly)
                ? <Fragment>
                     <div className='slds-m-top_medium slds-size_1-of-3'>
                      <SelectReviewer
                        label='Assign review to'
                        value={this.state.reviewer}
                        responsibility='reviews_loanapplications'
                        exclude={[localStorage.getItem('userUUID')]}
                        disabled={finalisedButQualifiedOutcome || readOnly || this.outcomeUnchanged()}
                        onChange={this.assignToReviewer}
                      />
                    </div>
                    <div className='slds-grid slds-align_absolute-center slds-m-top_large'>
                      <Button
                        minWidth={90}
                        onClick={this.handleClearClick}
                        disabled={!anyRequiredInputFieldsChanged || this.state.saveInProgress}>
                        {this.props.outcome ? 'Reset' : 'Clear'}
                      </Button>
                      <Button
                        type='brand'
                        minWidth={90}
                        onClick={this.handleFinaliseClick}
                        disabled={!allRequiredInputFieldsChanged || this.state.saveInProgress}
                        className='slds-m-left_small'>
                        {this.state.saveInProgress
                          ? 'Saving...'
                          : 'Finalise'
                        }
                      </Button>
                    </div>
                  </Fragment>
                : finalised &&
                  <div className='slds-m-top_small slds-text-color_weak'>
                    <SvgIcon
                      variant='utility'
                      symbol='info'
                      colour='light'
                      size='small'
                      className='slds-m-right_small'
                    />
                    {finalisedFeedback}
                  </div>
              }
            </div>
          )}
        </ReadOnlyContext.Consumer>
      </ExpandableOverviewItem>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/creditapplication-finalise/index.js